import { ChangeEvent, FC, FocusEventHandler, memo, useMemo } from 'react'
import { CompanyType, PlanningCategory, PlanningClassification } from '@leadgen/models/client'
import Box from '@mui/material/Box'
import Masonry from '@mui/lab/Masonry'
import Typography from '@mui/material/Typography'
import {
  CheckboxButtonContainer,
  CheckboxButtonContainerOption,
} from '../../../atoms/CheckboxButton'
import useMediaQuery from '@mui/material/useMediaQuery'
import {
  getPlanningCategoriesForClassifications,
  planningCategoriesByCompanyTypeMap,
  planningCategoryGroups,
  planningCategoryMap,
} from '../../../../constants/planningTaxonomy'

const CategoryGrid: FC<{
  companyType: CompanyType
  planningCategories: PlanningCategory[]
  planningClassifications: PlanningClassification[]
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  onBlur?: FocusEventHandler<HTMLElement>
}> = ({ companyType, planningCategories, planningClassifications, onChange, onBlur }) => {
  const sm = useMediaQuery(theme => theme.breakpoints.down('sm'))
  const md = useMediaQuery(theme => theme.breakpoints.between('sm', 'md'))
  const lg = useMediaQuery(theme => theme.breakpoints.between('md', 'lg'))
  const xl = useMediaQuery(theme => theme.breakpoints.between('lg', 'xl'))
  const xxl = useMediaQuery(theme => theme.breakpoints.between('xl', 'widescreen'))
  const widescreen = useMediaQuery(theme => theme.breakpoints.up('widescreen'))
  const selectedPlanningCategoriesSet = useMemo(
    () => new Set(planningCategories),
    [planningCategories],
  )
  const visiblePlanningCategoriesSet = useMemo(() => {
    const allowedByCompanyType = new Set(planningCategoriesByCompanyTypeMap[companyType] || [])
    return new Set(
      getPlanningCategoriesForClassifications(planningClassifications).filter(planningCategory =>
        allowedByCompanyType.has(planningCategory),
      ),
    )
  }, [companyType, planningClassifications])
  const visibleCategoryGroups = useMemo(
    () =>
      planningCategoryGroups
        .map(group => ({
          ...group,
          options: group.options.filter(option => visiblePlanningCategoriesSet.has(option)),
        }))
        .filter(group => group.options.length),
    [visiblePlanningCategoriesSet],
  )

  return (
    <Box
      component={'ul'}
      sx={{
        padding: 2,
        margin: 0,
      }}
    >
      {!planningClassifications.length && (
        <Typography variant="body2" color="text.secondary">
          Select one or more application classifications above to see relevant work categories.
        </Typography>
      )}
      {!!planningClassifications.length && !visibleCategoryGroups.length && (
        <Typography variant="body2" color="text.secondary">
          No work categories are available for this business type and classification selection.
        </Typography>
      )}
      {!!visibleCategoryGroups.length && (
        <Masonry
          columns={sm ? 1 : md ? 2 : lg ? 1 : xl ? 3 : xxl ? 3 : widescreen ? 4 : 100}
          spacing={1}
        >
          {visibleCategoryGroups.map(group => (
            <CheckboxButtonContainer key={group.id} label={group.title} tooltip={group.description}>
              {group.options.map(planningCategory => (
                <CheckboxButtonContainerOption
                  key={planningCategory}
                  onBlur={onBlur}
                  onChange={onChange}
                  value={planningCategory}
                  checked={selectedPlanningCategoriesSet.has(planningCategory)}
                  label={planningCategoryMap[planningCategory].title}
                  tooltip={planningCategoryMap[planningCategory].description}
                />
              ))}
            </CheckboxButtonContainer>
          ))}
        </Masonry>
      )}
    </Box>
  )
}

export default memo(CategoryGrid)
